import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {AuthService, UserService, AdminService} from '@/shared/services';
import {LoginRequest, RegistrationRequest, TwoFactorVerificationRequest, Patient, Recommendation} from '@/shared/types/api';
import {logError} from '@/shared/utils/logger';

// Query Keys - Centralized for cache management
export const queryKeys = {
    // User data
    userData: ['user', 'data'] as const,
    userProfile: ['user', 'profile'] as const,
    userPreferences: ['user', 'preferences'] as const,
    subPatients: ['user', 'patients'] as const,

    // Tests and results
    tests: ['tests'] as const,
    test: (id: number) => ['tests', id] as const,
    testsByPatient: (patientId: number) => ['tests', 'patient', patientId] as const,

    // Recommendations
    recommendations: ['recommendations'] as const,
    testRecommendations: (testId: number) => ['recommendations', 'test', testId] as const,

    // Appointments
    appointments: ['appointments'] as const,

    // Admin
    adminUsers: ['admin', 'users'] as const,
    adminRecommendations: ['admin', 'recommendations'] as const,
    adminStats: ['admin', 'stats'] as const,
    adminAuditLogs: ['admin', 'audit-logs'] as const,
} as const;

// ============================================================================
// USER DATA HOOKS
// ============================================================================

/**
 * Hook to fetch all user data (tests, results, recommendations)
 */
export const useUserData = (enabled = true) => {
    return useQuery({
        queryKey: queryKeys.userData,
        queryFn: UserService.getUserData,
        enabled: enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: (failureCount, error: any) => {
            return failureCount < 3 && error.status !== 401;
        },
    });
};

/**
 * Hook to fetch user profile
 */
export const useUserProfile = () => {
    return useQuery({
        queryKey: queryKeys.userProfile,
        queryFn: AuthService.getCurrentUser,
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to fetch user preferences
 */
export const useUserPreferences = () => {
    return useQuery({
        queryKey: queryKeys.userPreferences,
        queryFn: UserService.getPreferences,
        staleTime: 15 * 60 * 1000, // 15 minutes
    });
};

/**
 * Hook to fetch sub-patients
 */
export const useSubPatients = () => {
    return useQuery({
        queryKey: queryKeys.subPatients,
        queryFn: UserService.getSubPatients,
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to fetch specific test
 */
export const useTest = (testId: number, enabled = true) => {
    return useQuery({
        queryKey: queryKeys.test(testId),
        queryFn: () => UserService.getTest(testId),
        enabled: enabled && !!testId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to fetch tests for specific patient
 */
export const useTestsForPatient = (patientId: number, enabled = true) => {
    return useQuery({
        queryKey: queryKeys.testsByPatient(patientId),
        queryFn: () => UserService.getTestsForPatient(patientId),
        enabled: enabled && !!patientId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to fetch recommendations
 */
export const useRecommendations = () => {
    return useQuery({
        queryKey: queryKeys.recommendations,
        queryFn: UserService.getRecommendations,
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to fetch recommendations for specific test
 */
export const useTestRecommendations = (testId: number, enabled = true) => {
    return useQuery({
        queryKey: queryKeys.testRecommendations(testId),
        queryFn: () => UserService.getRecommendationsForTest(testId),
        enabled: enabled && !!testId,
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to fetch appointments
 */
export const useAppointments = () => {
    return useQuery({
        queryKey: queryKeys.appointments,
        queryFn: UserService.getAppointments,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Hook for user login
 */
export const useLogin = () => {
    return useMutation({
        mutationFn: (credentials: LoginRequest) => AuthService.login(credentials),
        onError: (error) => {
            logError('Login failed', error);
        },
    });
};

/**
 * Hook for user registration
 */
export const useRegister = () => {
    return useMutation({
        mutationFn: (userData: RegistrationRequest) => AuthService.register(userData),
        onError: (error) => {
            logError('Registration failed', error);
        },
    });
};

/**
 * Hook for two-factor verification
 */
export const useTwoFactorVerification = () => {
    return useMutation({
        mutationFn: ({mode, data}: {mode: 'SMS' | 'EMAIL' | 'TOTP'; data: TwoFactorVerificationRequest}) => {
            switch (mode) {
                case 'SMS':
                    return AuthService.verifySmsCode(data);
                case 'EMAIL':
                    return AuthService.verifyEmailCode(data);
                case 'TOTP':
                    return AuthService.verifyTotpCode(data.verificationCode);
                default:
                    throw new Error('Invalid 2FA mode');
            }
        },
    });
};

/**
 * Hook for updating user profile
 */
export const useUpdateProfile = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (updates: any) => AuthService.updateProfile(updates),
        onSuccess: (data) => {
            // Update cache with new profile data
            queryClient.setQueryData(queryKeys.userProfile, data);
        },
    });
};

/**
 * Hook for updating user preferences
 */
export const useUpdatePreferences = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (preferences: Record<string, any>) => UserService.updatePreferences(preferences),
        onSuccess: () => {
            // Invalidate preferences cache
            queryClient.invalidateQueries({queryKey: queryKeys.userPreferences});
        },
    });
};

/**
 * Hook for adding sub-patient
 */
export const useAddSubPatient = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (patientData: Omit<Patient, 'id'>) => UserService.addSubPatient(patientData),
        onSuccess: () => {
            // Invalidate sub-patients cache
            queryClient.invalidateQueries({queryKey: queryKeys.subPatients});
        },
    });
};

/**
 * Hook for updating sub-patient
 */
export const useUpdateSubPatient = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({patientId, updates}: {patientId: number; updates: Partial<Patient>}) =>
            UserService.updateSubPatient(patientId, updates),
        onSuccess: () => {
            // Invalidate sub-patients cache
            queryClient.invalidateQueries({queryKey: queryKeys.subPatients});
        },
    });
};

/**
 * Hook for dismissing recommendations
 */
export const useDismissRecommendation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (recommendationId: number) => UserService.dismissRecommendation(recommendationId),
        onSuccess: () => {
            // Invalidate recommendations cache
            queryClient.invalidateQueries({queryKey: queryKeys.recommendations});
        },
    });
};

/**
 * Hook for scheduling appointments
 */
export const useScheduleAppointment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (appointmentData: any) => UserService.scheduleAppointment(appointmentData),
        onSuccess: () => {
            // Invalidate appointments cache
            queryClient.invalidateQueries({queryKey: queryKeys.appointments});
        },
    });
};

/**
 * Hook for uploading profile photo
 */
export const useUploadProfilePhoto = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({file, onProgress}: {file: File; onProgress?: (progress: number) => void}) =>
            UserService.uploadProfilePhoto(file, onProgress),
        onSuccess: () => {
            // Invalidate user profile cache
            queryClient.invalidateQueries({queryKey: queryKeys.userProfile});
        },
    });
};

// ============================================================================
// ADMIN HOOKS (only for admin users)
// ============================================================================

/**
 * Hook for admin user search
 */
export const useAdminUserSearch = () => {
    return useMutation({
        mutationFn: (searchCriteria: any) => AdminService.searchUsers(searchCriteria),
    });
};

/**
 * Hook for admin recommendations
 */
export const useAdminRecommendations = () => {
    return useQuery({
        queryKey: queryKeys.adminRecommendations,
        queryFn: () => AdminService.getAllRecommendations(),
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook for creating admin recommendations
 */
export const useCreateRecommendation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (recommendation: Omit<Recommendation, 'id'>) => AdminService.createRecommendation(recommendation),
        onSuccess: () => {
            // Invalidate admin recommendations cache
            queryClient.invalidateQueries({queryKey: queryKeys.adminRecommendations});
        },
    });
};

/**
 * Hook for admin system stats
 */
export const useAdminStats = () => {
    return useQuery({
        queryKey: queryKeys.adminStats,
        queryFn: AdminService.getSystemStats,
        staleTime: 2 * 60 * 1000, // 2 minutes
    });
};

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to invalidate all user-related queries (useful for logout)
 */
export const useInvalidateUserQueries = () => {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({queryKey: ['user']});
        queryClient.invalidateQueries({queryKey: ['tests']});
        queryClient.invalidateQueries({queryKey: ['recommendations']});
        queryClient.invalidateQueries({queryKey: ['appointments']});
    };
};

export default {
    // User hooks
    useUserData,
    useUserProfile,
    useUserPreferences,
    useSubPatients,
    useTest,
    useTestsForPatient,
    useRecommendations,
    useTestRecommendations,
    useAppointments,

    // Mutation hooks
    useLogin,
    useRegister,
    useTwoFactorVerification,
    useUpdateProfile,
    useUpdatePreferences,
    useAddSubPatient,
    useUpdateSubPatient,
    useDismissRecommendation,
    useScheduleAppointment,
    useUploadProfilePhoto,

    // Admin hooks
    useAdminUserSearch,
    useAdminRecommendations,
    useCreateRecommendation,
    useAdminStats,

    // Utility hooks
    useInvalidateUserQueries,
};
